import { ChangeDetectionStrategy, Component, computed, inject, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { FikoYTKokBilesenComponent } from '@/src/service/kok-bilesen/kok-bilesen.component';
import { HmiLookupService } from '../../../../services/hmi-lookup.service';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';

@Component({
  selector: 'is-emri-edit',
  standalone: true,
  imports: [FormsModule, NzInputModule, NzInputNumberModule, NzSelectModule, NzDatePickerModule,
    CdkTextareaAutosize],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ie-edit">
      <div class="ie-baslik">
        <div class="ie-baslik-ikon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <path d="M9 15l2 2 4-4"/>
          </svg>
        </div>
        <span>İş Emri Bilgileri</span>
      </div>

      <div class="ie-form">
        <div class="form-grup">
          <label>Ürün Adı <span class="zorunlu">*</span></label>
          <div class="input-wrap">
            <input nz-input autocomplete="off" readonly onfocus="this.removeAttribute('readonly')"
              [ngModel]="p()?.urunAdi || ''"
              (ngModelChange)="guncelle('urunAdi', $event)"
              placeholder="Ürün adını girin..."/>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="form-grup">
          <label>Makine</label>
          <div class="input-wrap">
            <nz-select
              [ngModel]="p()?.makine"
              (ngModelChange)="guncelle('makine', $event)"
              nzPlaceHolder="Makine seçin..."
              nzAllowClear nzShowSearch nzServerSearch
              (nzOnSearch)="lookup.makineleriYukle($event)"
              (nzOpenChange)="$event && lookup.makineleriYukle()"
              style="width:100%"
              [nzCustomTemplate]="ieMakineTpl">
              @for (m of lookup.makineler(); track m._id) {
                <nz-option [nzValue]="m.makineAdi" [nzLabel]="m.makineAdi" nzCustomContent>
                  <div class="sel-item"><span class="sel-emoji">⚙️</span><div class="sel-bilgi"><span class="sel-ad">{{ m.makineAdi }}</span><span class="sel-detay">{{ m.makineKodu || '' }}{{ m.tip ? ' · ' + m.tip : '' }}</span></div></div>
                </nz-option>
              }
            </nz-select>
            <ng-template #ieMakineTpl let-selected>
              <span class="sel-item"><span class="sel-emoji">⚙️</span><span class="sel-ad">{{ selected.nzLabel }}</span></span>
            </ng-template>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="form-satir">
          <div class="form-grup f1">
            <label>Miktar</label>
            <div class="input-wrap">
              <nz-input-number
                [ngModel]="p()?.miktar || 1"
                (ngModelChange)="guncelle('miktar', $event)"
                [nzMin]="1" style="width:100%">
              </nz-input-number>
              <div class="input-glow"></div>
            </div>
          </div>
          <div class="form-grup f1">
            <label>Birim</label>
            <div class="input-wrap">
              <nz-select
                [ngModel]="p()?.birim || 'Adet'"
                (ngModelChange)="guncelle('birim', $event)"
                style="width:100%"
                [nzCustomTemplate]="ieBirimTpl">
                <nz-option nzValue="Adet" nzLabel="Adet" nzCustomContent>
                  <div class="sel-item"><span class="sel-dot" style="background:#8b5cf6"></span><span class="sel-ad">Adet</span></div>
                </nz-option>
                <nz-option nzValue="Kg" nzLabel="Kg" nzCustomContent>
                  <div class="sel-item"><span class="sel-dot" style="background:#3b82f6"></span><span class="sel-ad">Kg</span></div>
                </nz-option>
                <nz-option nzValue="Metre" nzLabel="Metre" nzCustomContent>
                  <div class="sel-item"><span class="sel-dot" style="background:#10b981"></span><span class="sel-ad">Metre</span></div>
                </nz-option>
                <nz-option nzValue="Litre" nzLabel="Litre" nzCustomContent>
                  <div class="sel-item"><span class="sel-dot" style="background:#06b6d4"></span><span class="sel-ad">Litre</span></div>
                </nz-option>
                <nz-option nzValue="Ton" nzLabel="Ton" nzCustomContent>
                  <div class="sel-item"><span class="sel-dot" style="background:#f59e0b"></span><span class="sel-ad">Ton</span></div>
                </nz-option>
              </nz-select>
              <ng-template #ieBirimTpl let-selected>
                <span class="sel-item"><span class="sel-dot" style="background:#8b5cf6"></span><span class="sel-ad">{{ selected.nzLabel }}</span></span>
              </ng-template>
              <div class="input-glow"></div>
            </div>
          </div>
        </div>

        <div class="ie-ayirici"></div>

        <div class="form-grup">
          <label>Öncelik</label>
          <div class="oncelik-secici">
            @for (o of oncelikler; track o.deger) {
              <button class="oncelik-btn"
                [class.secili]="p()?.oncelik === o.deger"
                [style.--_o]="o.renk"
                (click)="guncelle('oncelik', o.deger)">
                <span class="oncelik-dot"></span>
                {{ o.deger }}
              </button>
            }
          </div>
        </div>

        <div class="form-grup">
          <label>Hedef Tarih</label>
          <div class="input-wrap">
            <nz-date-picker
              [ngModel]="p()?.hedefTarih"
              (ngModelChange)="guncelle('hedefTarih', $event)"
              nzPlaceHolder="Tarih seçin..."
              style="width:100%">
            </nz-date-picker>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="ie-ayirici"></div>

        <div class="form-grup">
          <label>Sorumlu</label>
          <div class="input-wrap">
            <nz-select
              [ngModel]="p()?.sorumlu"
              (ngModelChange)="guncelle('sorumlu', $event)"
              nzPlaceHolder="Sorumlu seçin..."
              nzAllowClear nzShowSearch
              style="width:100%"
              [nzCustomTemplate]="ieSorumluTpl">
              @for (kisi of lookup.personeller(); track kisi._id) {
                <nz-option [nzValue]="kisi.ad + ' ' + kisi.soyad" [nzLabel]="kisi.ad + ' ' + kisi.soyad" nzCustomContent>
                  <div class="sel-item"><span class="sel-emoji">👤</span><div class="sel-bilgi"><span class="sel-ad">{{ kisi.ad }} {{ kisi.soyad }}</span><span class="sel-detay">{{ kisi.unvan || kisi.gorev || '' }}{{ kisi.sicilNo ? ' · ' + kisi.sicilNo : '' }}</span></div></div>
                </nz-option>
              }
            </nz-select>
            <ng-template #ieSorumluTpl let-selected>
              <span class="sel-item"><span class="sel-emoji">👤</span><span class="sel-ad">{{ selected.nzLabel }}</span></span>
            </ng-template>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="form-grup">
          <label>Açıklama</label>
          <div class="input-wrap">
            <textarea nz-input autocomplete="off" readonly onfocus="this.removeAttribute('readonly')"
              [ngModel]="p()?.aciklama || ''"
              (ngModelChange)="guncelle('aciklama', $event)"
              placeholder="İş emri açıklaması..."
              cdkTextareaAutosize [cdkAutosizeMinRows]="2" [cdkAutosizeMaxRows]="4">
            </textarea>
            <div class="input-glow"></div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`

    :host { display: block; }

    .ie-edit { display: flex; flex-direction: column; gap: 0; padding: 0; }

    .ie-baslik {
      display: flex; align-items: center; gap: 10px;
      padding: 12px 16px; margin-bottom: 4px; border-radius: 10px;
      background: linear-gradient(135deg, var(--py-arkaplan-soluk, rgba(0,0,0,0.03)), transparent);
    }

    .ie-baslik-ikon {
      width: 32px; height: 32px; border-radius: 8px;
      display: flex; align-items: center; justify-content: center;
      background: linear-gradient(135deg, #1e3a5f, #2563eb);
      color: #fff; flex-shrink: 0;
      box-shadow: 0 2px 8px rgba(37, 99, 235, 0.35);
      position: relative;
    }

    .ie-baslik-ikon::after {
      content: ''; position: absolute; inset: 0; border-radius: inherit;
      background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%);
      background-size: 200% 100%;
      animation: shimmer 3s infinite linear;
      pointer-events: none;
    }

    .ie-baslik span { font-size: 13px; font-weight: 600; color: var(--py-metin, #333); letter-spacing: 0.3px; }

    .ie-form { display: flex; flex-direction: column; gap: 14px; padding: 4px 2px; }

    .ie-ayirici {
      height: 1px;
      background: linear-gradient(90deg, transparent, var(--py-kenarlik, #d9d9d9) 20%, var(--py-kenarlik, #d9d9d9) 80%, transparent);
      opacity: 0.4; margin: 2px 0;
    }

    .form-satir { display: flex; gap: 12px; }
    .f1 { flex: 1; }

    .form-grup { display: flex; flex-direction: column; gap: 5px; }

    .form-grup label {
      font-size: 11px; font-weight: 500;
      color: var(--py-metin-ucuncul, rgba(0,0,0,0.45));
      text-transform: uppercase; letter-spacing: 0.6px;
      padding-left: 2px; transition: color 0.2s ease;
    }
    .form-grup:focus-within label { color: var(--py-link, #1890ff); }

    .zorunlu { color: #ff4d4f; font-size: 13px; line-height: 1; vertical-align: middle; }

    .input-wrap { position: relative; }
    .input-glow {
      position: absolute; bottom: 0; left: 50%; width: 0; height: 2px; border-radius: 1px;
      background: linear-gradient(90deg, #1e3a5f, #2563eb, #1e3a5f);
      transition: width 0.35s cubic-bezier(0.4,0,0.2,1), left 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.3s ease;
      opacity: 0; pointer-events: none; z-index: 1;
    }
    .input-wrap:focus-within .input-glow { width: 100%; left: 0; opacity: 1; }

    .input-wrap input[nz-input],
    .input-wrap textarea[nz-input] {
      background: var(--py-arkaplan-soluk, rgba(0,0,0,0.02));
      border: 1px solid var(--py-kenarlik, #d9d9d9);
      border-radius: 8px; padding: 7px 11px; font-size: 13px;
      color: var(--py-metin, #333);
      transition: border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
    }
    .input-wrap input[nz-input]:hover,
    .input-wrap textarea[nz-input]:hover { border-color: var(--py-kenarlik-hover, #b3b3b3); background: var(--py-arkaplan-hover, #f5f5f5); }
    .input-wrap input[nz-input]:focus,
    .input-wrap textarea[nz-input]:focus { border-color: var(--py-kenarlik-aktif, #1890ff); background: var(--py-input-arka, #fff); box-shadow: 0 0 0 2px rgba(24,144,255,0.1); outline: none; }

    .input-wrap ::ng-deep .ant-select-selector {
      background: var(--py-arkaplan-soluk, rgba(0,0,0,0.02)) !important;
      border: 1px solid var(--py-kenarlik, #d9d9d9) !important;
      border-radius: 8px !important; min-height: 34px;
      transition: border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
    }
    .input-wrap:hover ::ng-deep .ant-select-selector { border-color: var(--py-kenarlik-hover, #b3b3b3) !important; background: var(--py-arkaplan-hover, #f5f5f5) !important; }
    .input-wrap:focus-within ::ng-deep .ant-select-selector { border-color: var(--py-kenarlik-aktif, #1890ff) !important; background: var(--py-input-arka, #fff) !important; box-shadow: 0 0 0 2px rgba(24,144,255,0.1) !important; }

    .input-wrap ::ng-deep .ant-picker {
      background: var(--py-arkaplan-hover, rgba(0,0,0,.02)) !important;
      border: 1px solid var(--py-kenarlik, rgba(0,0,0,.1)) !important;
      border-radius: 8px !important;
      min-height: 34px;
      width: 100% !important;
      font-size: 13px !important;
      transition: border-color .25s, box-shadow .25s, background .25s !important;
    }
    .input-wrap ::ng-deep .ant-picker input { font-size: 13px !important; color: var(--py-metin) !important; }
    .input-wrap:hover ::ng-deep .ant-picker { border-color: rgba(102,126,234,.3) !important; background: var(--py-arkaplan-hover, rgba(0,0,0,.01)) !important; }
    .input-wrap:focus-within ::ng-deep .ant-picker,
    .input-wrap ::ng-deep .ant-picker-focused { border-color: rgba(102,126,234,.5) !important; background: transparent !important; box-shadow: 0 0 0 2px rgba(102,126,234,.08) !important; }

    .input-wrap ::ng-deep .ant-input-number {
      background: var(--py-arkaplan-soluk, rgba(0,0,0,0.02)) !important;
      border: 1px solid var(--py-kenarlik, #d9d9d9) !important;
      border-radius: 8px !important;
      transition: border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
    }
    .input-wrap:hover ::ng-deep .ant-input-number { border-color: var(--py-kenarlik-hover, #b3b3b3) !important; }
    .input-wrap:focus-within ::ng-deep .ant-input-number { border-color: var(--py-kenarlik-aktif, #1890ff) !important; box-shadow: 0 0 0 2px rgba(24,144,255,0.1) !important; }

    .oncelik-secici { display: flex; gap: 8px; }
    .oncelik-btn {
      --_o: #52c41a;
      flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px;
      padding: 8px 12px; border-radius: 8px; border: 1px solid var(--py-kenarlik, #d9d9d9);
      background: var(--py-arkaplan-soluk, rgba(0,0,0,0.02));
      font-size: 12px; font-weight: 500; color: var(--py-metin-ikincil, rgba(0,0,0,0.65));
      cursor: pointer; transition: all 0.25s ease;
    }
    .oncelik-btn:hover { border-color: var(--_o); color: var(--_o); }
    .oncelik-btn.secili {
      border-color: var(--_o); color: var(--_o);
      background: color-mix(in srgb, var(--_o) 8%, transparent);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--_o) 12%, transparent);
    }
    .oncelik-dot {
      width: 8px; height: 8px; border-radius: 50%; background: var(--_o);
      box-shadow: 0 0 6px var(--_o);
    }

    @keyframes shimmer {
      0% { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
  `],
})
export class IsEmriEditComponent extends FikoYTKokBilesenComponent {
  lookup = inject(HmiLookupService);
  parametreler = model<any>();

  constructor() { super(); this.lookup.personelleriYukle(); this.lookup.makineleriYukle(); }

  p = computed(() => this.parametreler()?.ek || {});

  oncelikler = [
    { deger: 'Normal', renk: '#52c41a' },
    { deger: 'Acil', renk: '#fa8c16' },
    { deger: 'Kritik', renk: '#ff4d4f' },
  ];

  guncelle(alan: string, deger: any) {
    const params = this.parametreler();
    if (params?.ek) {
      params.ek[alan] = deger;
      this.parametreler.set({ ...params });
    }
  }
}
